import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search, UserCheck } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { driverProfileName } from '@/lib/driverProfile';

export default function ContractWitnesses() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const { data: countData } = useQuery({
        queryKey: ['contract-witnesses-count', debouncedSearch],
        queryFn: async () => {
            let query = supabase.from('contract_witnesses').select('*', { count: 'exact', head: true });
            if (debouncedSearch) {
                query = query.or(`name.ilike.%${debouncedSearch}%,phone.ilike.%${debouncedSearch}%`);
            }
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({
        totalCount: countData || 0
    });

    useEffect(() => {
        resetPage();
    }, [debouncedSearch]);

    const { data: witnesses, isLoading } = useQuery({
        queryKey: ['contract-witnesses', currentPage, debouncedSearch],
        queryFn: async () => {
            let query = supabase
                .from('contract_witnesses')
                // `admin_id` IS SELECTED SO THE DRIVER'S OWN ROW CAN BE PICKED OUT — see
                // `driverProfileOf`; the embed can carry an admin's test profile beside it.
                .select('*, contracts(drivers(profiles(id, admin_id, first_name, last_name)))')
                .order('created_at', { ascending: false });
            if (debouncedSearch) {
                query = query.or(`name.ilike.%${debouncedSearch}%,phone.ilike.%${debouncedSearch}%`);
            }
            query = query.range(from, to);
            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <UserCheck className="h-8 w-8" />
                        <h1 className="text-4xl font-semibold tracking-tight">Contract Witnesses</h1>
                    </div>
                    <p className="text-muted-foreground">Contract witness records.</p>
                </div>
            </div>

            <Card className="border-0">
                <CardHeader>
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input placeholder="Search by name or phone..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                </CardHeader>
                <CardContent>
                        <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
                    {isLoading ? (
                        <Skeleton className="h-48 w-full" />
                    ) : witnesses && witnesses.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Contract</TableHead>
                                    <TableHead>Driver</TableHead>
                                    <TableHead>Witness Name</TableHead>
                                    <TableHead>Phone</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {witnesses.map((w) => {
                                    /*
                                     * BOTH HOPS ARE COLLECTIONS. `contracts.witness_id` points AT
                                     * this table, so `contracts` arrives as a list, and `profiles`
                                     * hangs off `drivers` the same way — reading `.drivers` off the
                                     * list gave `undefined` and this column printed a dash on every
                                     * row. The profile is then picked by `driverProfileName`, which
                                     * excludes an admin's test profile against the driver's id.
                                     */
                                    const contract = Array.isArray(w.contracts) ? w.contracts[0] : w.contracts;
                                    const driver = driverProfileName(contract?.drivers?.profiles) ?? '-';
                                    return (
                                        <TableRow key={w.id}>
                                            <TableCell>{new Date(w.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell>#{w.contract_id}</TableCell>
                                            <TableCell>{driver}</TableCell>
                                            <TableCell>{w.name || '-'}</TableCell>
                                            <TableCell>{w.phone || '-'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className="text-center text-muted-foreground py-8">No witnesses found</p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
